import { useCallback, useState } from 'react';
import type { Task, TeamMemberView } from '../types';
import type { ContextMenuItem } from '../components/ContextMenu';
import * as api from '../utils/apiClient';
import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';

interface ContextState {
  x: number;
  y: number;
  task: Task;
}

export interface TaskContextHandlers {
  contextMenu: ContextState | null;
  moveDialogFor: Task | null;
  openContextMenu: (task: Task, e: React.MouseEvent) => void;
  closeContextMenu: () => void;
  closeMoveDialog: () => void;
  buildMenuItems: (
    task: Task,
    onReplace: (t: Task) => void,
    onRemove: (id: string) => void,
    onDelete?: (task: Task) => void
  ) => ContextMenuItem[];
  onMoveScope: (task: Task, toTeamId: string, onReplace: (t: Task) => void, onRemove: (id: string) => void) => Promise<void>;
}

export function useTaskContextMenu(): TaskContextHandlers {
  const [contextMenu, setContextMenu] = useState<ContextState | null>(null);
  const [moveDialogFor, setMoveDialogFor] = useState<Task | null>(null);
  const [reclassifying, setReclassifying] = useState<string | null>(null);
  const { members } = useTeam();
  const { user } = useAuth();

  const openContextMenu = useCallback((task: Task, e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, task });
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);
  const closeMoveDialog = useCallback(() => setMoveDialogFor(null), []);

  const onMoveScope = useCallback(
    async (
      task: Task,
      toTeamId: string,
      onReplace: (t: Task) => void,
      onRemove: (id: string) => void
    ) => {
      if (toTeamId === task.teamId) return;
      const updated = await api.moveTask(task.teamId, task.taskId, toTeamId);
      onReplace(updated);
      onRemove(task.taskId);
    },
    []
  );

  const buildMenuItems = useCallback(
    (
      task: Task,
      onReplace: (t: Task) => void,
      onRemove: (id: string) => void,
      onDelete?: (task: Task) => void
    ): ContextMenuItem[] => {
      const isCompleted = task.status === 'completed';
      const isCreator = !!user && task.createdBy === user.userId;
      const reassignables: TeamMemberView[] = members.filter(
        (m) => m.userId !== task.assigneeId
      );

      const items: ContextMenuItem[] = [
        {
          icon: '✅',
          label: isCompleted ? 'Reopen' : 'Complete',
          onClick: async () => {
            const updated = await api.updateTask(task.teamId, task.taskId, {
              status: isCompleted ? 'open' : 'completed',
            });
            onReplace(updated);
          },
        },
        {
          icon: '📌',
          label: task.pinned ? 'Unpin' : 'Pin',
          onClick: async () => {
            const updated = await api.updateTask(task.teamId, task.taskId, {
              pinned: !task.pinned,
            });
            onReplace(updated);
          },
        },
        {
          icon: '⏰',
          label: 'Snooze 1 day',
          onClick: async () => {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const updated = await api.updateTask(task.teamId, task.taskId, {
              dueDate: tomorrow.toISOString(),
            });
            onReplace(updated);
          },
        },
      ];

      if (members.length > 1 && !task.private) {
        for (const m of reassignables) {
          items.push({
            icon: '👤',
            label: `Reassign to ${m.displayName}`,
            onClick: async () => {
              const updated = await api.updateTask(task.teamId, task.taskId, {
                assigneeId: m.userId,
              });
              onReplace(updated);
            },
          });
        }
      }

      items.push({
        icon: '➡',
        label: 'Move to…',
        onClick: () => setMoveDialogFor(task),
      });

      items.push({
        icon: '🧠',
        label: reclassifying === task.taskId ? 'Reclassifying…' : 'Reclassify with AI',
        disabled: reclassifying === task.taskId,
        onClick: async () => {
          setReclassifying(task.taskId);
          try {
            const updated = await api.reclassifyTask(task.teamId, task.taskId);
            if (updated.teamId !== task.teamId) {
              onRemove(task.taskId);
            }
            onReplace(updated);
          } finally {
            setReclassifying(null);
          }
        },
      });

      if (isCreator) {
        items.push({
          icon: task.private ? '🔓' : '🔒',
          label: task.private ? 'Make shared' : 'Make private',
          onClick: async () => {
            const updated = await api.updateTask(task.teamId, task.taskId, {
              private: !task.private,
            });
            onReplace(updated);
          },
        });
      }

      items.push({ label: '', divider: true });
      items.push({
        icon: '🗑',
        label: 'Delete',
        danger: true,
        onClick: () => {
          if (onDelete) {
            onDelete(task);
          } else {
            void api.deleteTask(task.teamId, task.taskId).then(() => onRemove(task.taskId));
          }
        },
      });

      return items;
    },
    [reclassifying, members, user]
  );

  return {
    contextMenu,
    moveDialogFor,
    openContextMenu,
    closeContextMenu,
    closeMoveDialog,
    buildMenuItems,
    onMoveScope,
  };
}
